import { type FC, type ReactNode, useState, useEffect } from 'react'
import { CopilotKit, useCopilotAction, useCopilotReadable } from '@copilotkit/react-core'
import { isValidConfig, configClient } from '@egw/config'
import { Logger } from '@egw/logger'
import { notifications, NOTIFICATIONS } from '@egw/notifications'
import { useAuth } from '@egw/auth'
import { type Config, configSchema } from '../../config'
import { type UseCopilotReadableOptions } from '../../models'
import { CopilotErrorBoundary } from '../../components/copilot-error-boundary'

const log = new Logger('Feature AI Chat - Copilot Kit')

export const threadId = crypto.randomUUID()

interface Agent {
  id: string
  name: string
  description?: string
}

// TODO: remove this after testing
const agents: Agent[] = [
  { id: 'advice', name: 'Default' },
  { id: 'no_guardrails', name: 'Without Guardrail' },
]

interface AgentSelectProps {
  selectedAgent: string
  onAgentChange: (agentId: string) => void
}

// TODO: remove this after testing
export const AgentSelect: FC<AgentSelectProps> = ({ selectedAgent, onAgentChange }) => {
  const [isOpen, setIsOpen] = useState(false)

  // TODO: remove this after testing
  const selectedAgentData = agents.find((agent) => agent.id === selectedAgent)

  // TODO: remove this after testing
  const handleAgentChange = (agentId: string): void => {
    onAgentChange(agentId)
    notifications.dispatch(NOTIFICATIONS.AI_CHAT.AGENT_CHANGED, { agentId })
    setIsOpen(false)
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen)
        }}
        className="flex items-center justify-between w-full px-md py-3xs border border-gray-200 rounded-lg hover:border-gray-300"
        data-testid="agent-select-button"
      >
        <span className="typo-primary-sm-regular text-text-header">{selectedAgentData?.name}</span>
      </button>

      {isOpen ? (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg z-10 max-h-48 overflow-y-auto">
          {agents.map((agent) => (
            <button
              key={agent.id}
              type="button"
              onClick={() => {
                handleAgentChange(agent.id)
              }}
              className={`w-full typo-primary-sm-regular px-xs py-2xs text-left hover:bg-gray-50 ${agent.id === selectedAgent ? 'bg-gray-100' : ''
                }`}
              data-testid={`agent-option-${agent.id}`}
            >
              <div className="text-text-header">{agent.name}</div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

interface AiChatProviderProps {
  children: ReactNode
  config: Config
}

/**
 * Check if CopilotKit is available and should be initialized
 * Function reads config
 * @returns true if CopilotKit should be initialized, false otherwise
 */
export const isCopilotKitRuntimeAvailable = (): boolean => {
  try {
    const appConfig = configClient.getConfig() as {
      env?: string
      aiChat?: { runtimeUrl?: string; agent?: string }
    }

    // Check if aiChat config has required values
    if (!appConfig.aiChat?.runtimeUrl || appConfig.aiChat.runtimeUrl.trim() === '') {
      log.debug('CopilotKit runtimeUrl is empty in config, skipping initialization')
      return false
    }

    if (!appConfig.aiChat.agent || appConfig.aiChat.agent.trim() === '') {
      log.debug('CopilotKit agent is empty in config, skipping initialization')
      return false
    }
  } catch (error) {
    log.debug('Could not access app config to check CopilotKit availability', { error })
    return false
  }

  return true
}

export const AiChatProvider: FC<AiChatProviderProps> = ({ config, children }) => {
  // TODO: remove this after testing
  const [selectedAgent, setSelectedAgent] = useState<string>(() => {
    return localStorage.getItem('selected-agent') ?? 'advice'
  })

  const { isAuthenticated, accessToken } = useAuth()

  const [currentAccessToken, setCurrentAccessToken] = useState<string | undefined>(accessToken)

  // TODO: remove this after testing
  useEffect(() => {
    const handleAgentChanged = (data: unknown): void => {
      const eventData = data as { detail: { agentId: string } }
      const { agentId } = eventData.detail
      setSelectedAgent(agentId)
      localStorage.setItem('selected-agent', agentId)
    }

    notifications.on(NOTIFICATIONS.AI_CHAT.AGENT_CHANGED, handleAgentChanged)

    // Cleanup
    return () => {
      notifications.remove(NOTIFICATIONS.AI_CHAT.AGENT_CHANGED, handleAgentChanged)
    }
  }, [])

  // Subscribe to token refresh events - this is the key fix
  useEffect(() => {
    const handleSessionLoaded = (data: unknown): void => {
      const eventData = data as { detail: { accessToken?: string } }
      if (eventData.detail.accessToken) {
        setCurrentAccessToken(eventData.detail.accessToken)
      }
    }

    notifications.on(NOTIFICATIONS.AUTH.SESSION_LOADED, handleSessionLoaded)

    return () => {
      notifications.remove(NOTIFICATIONS.AUTH.SESSION_LOADED, handleSessionLoaded)
    }
  }, [])

  // Sync with accessToken from useAuth hook as fallback
  useEffect(() => {
    if (accessToken) {
      setCurrentAccessToken(accessToken)
    }
  }, [accessToken])

  if (!isValidConfig(config, configSchema)) {
    log.error('Configuration is invalid')
    throw new Error('Configuration is invalid')
  }

  if (!isCopilotKitRuntimeAvailable()) {
    log.debug('CopilotKit runtime is not available, rendering children without AI chat')
    return children
  }

  // Ensure the user is authenticated before making api calls to the copilot kit
  if (!isAuthenticated) {
    return children
  }

  const headers: Record<string, string> = currentAccessToken ? { Authorization: `Bearer ${currentAccessToken}` } : {}

  // Wrap CopilotKit in error boundary AND try-catch for initialization errors
  try {
    return (
      <CopilotErrorBoundary fallback={children}>
        <CopilotKit
          key="copilot-chat"
          threadId={threadId}
          showDevConsole={false}
          runtimeUrl={config.runtimeUrl}
          agent={selectedAgent ?? agents[0].id ?? config.agent}
          credentials="omit"
          headers={headers}
        >
          {children}
        </CopilotKit>
      </CopilotErrorBoundary>
    )
  } catch (error) {
    log.warn('Failed to initialize CopilotKit, continuing without AI chat:', error)
    return children
  }
}

export const useAiChatAction = (action: any) => {
  useCopilotAction(action)
}

export const useAiChatReadable = ({ description, value, ...props }: UseCopilotReadableOptions) => {
  useCopilotReadable({ description, value, ...props })
}
